import React, { useMemo, useState } from 'react';
import { Button } from '../../components/ui/button';
import FundingCommissionCard from '../../components/funding/FundingCommissionCard';
import FundingCommissionClawbackPanel from '../../components/funding/FundingCommissionClawbackPanel';
import FundingCommissionDetail from '../../components/funding/FundingCommissionDetail';
import FundingCommissionFilters from '../../components/funding/FundingCommissionFilters';
import FundingCommissionMetrics from '../../components/funding/FundingCommissionMetrics';
import FundingCommissionPayoutList from '../../components/funding/FundingCommissionPayoutList';
import FundingCommissionTable from '../../components/funding/FundingCommissionTable';
import FundingPanel from '../../components/funding/FundingPanel';
import FundingSectionPage from '../../components/funding/FundingSectionPage';
import FundingAIAdvisorActionLink from '../../components/funding/FundingAIAdvisorActionLink';
import { useFundingWorkspace } from '../../context/FundingWorkspaceContext';
import { useFundingCommissions } from '../../hooks/funding/useFundingCommissions';

const FundingCommissionsPage = () => {
  const { workspaceAgencyId, isPlatformContext, requiresAgencySelection } = useFundingWorkspace();
  const [viewMode, setViewMode] = useState('table');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [payoutStatus, setPayoutStatus] = useState('');
  const [clawbackRisk, setClawbackRisk] = useState('');
  const [assignedUserId, setAssignedUserId] = useState('');
  const [payoutDueFrom, setPayoutDueFrom] = useState('');
  const [payoutDueTo, setPayoutDueTo] = useState('');
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [savingId, setSavingId] = useState('');

  const agencyId = isPlatformContext ? workspaceAgencyId || undefined : workspaceAgencyId;

  const {
    commissions,
    loading,
    saving,
    error,
    update,
    remove,
  } = useFundingCommissions({
    agencyId,
    filters: {
      status,
      payoutStatus,
      clawbackRisk,
      assignedUserId,
      payoutDueFrom,
      payoutDueTo,
      search,
    },
    autoLoad: Boolean(agencyId) || isPlatformContext,
  });

  const filteredCommissions = useMemo(() => {
    if (!search.trim()) return commissions;
    const term = search.trim().toLowerCase();
    return commissions.filter((commission) => (
      (commission.clientName || '').toLowerCase().includes(term)
      || (commission.businessName || '').toLowerCase().includes(term)
      || (commission.lenderName || '').toLowerCase().includes(term)
      || (commission.dealId || '').toLowerCase().includes(term)
    ));
  }, [commissions, search]);

  const handleStatusChange = async (commission, nextStatus) => {
    if (!agencyId) return;
    setSavingId(commission.id);
    try {
      const payload = { status: nextStatus };
      if (nextStatus === 'Paid') payload.payout_status = 'Paid';
      if (nextStatus === 'Pending Payout') payload.payout_status = 'Due';
      const updated = await update(commission.id, payload, agencyId);
      if (selectedCommission?.id === commission.id) setSelectedCommission(updated);
    } finally {
      setSavingId('');
    }
  };

  const handleDelete = async (commission) => {
    if (!agencyId) return;
    const confirmed = window.confirm(`Void commission for ${commission.lenderName}?`);
    if (!confirmed) return;
    setSavingId(commission.id);
    try {
      await remove(commission.id, agencyId);
      if (selectedCommission?.id === commission.id) setSelectedCommission(null);
    } finally {
      setSavingId('');
    }
  };

  return (
    <FundingSectionPage
      title="Commissions"
      subtitle="Track projected, earned, pending payout, paid, and clawback-monitoring commission records."
      activeStage="funded"
      actions={(
        <div className="flex gap-2">
          <FundingAIAdvisorActionLink label="Review clawback risks" prompt="Find commission risks" />
          <Button variant="outline" className="rounded-xl" onClick={() => setViewMode(viewMode === 'table' ? 'cards' : 'table')}>
            {viewMode === 'table' ? 'Card view' : 'Table view'}
          </Button>
        </div>
      )}
    >
      <FundingPanel title="Commission summary" subtitle="Estimated and earned amounts across your commission book">
        <FundingCommissionMetrics commissions={filteredCommissions} />
      </FundingPanel>

      <FundingPanel title="Commission records" subtitle="Payout tracking and clawback monitoring for funded deals">
        {requiresAgencySelection ? (
          <p className="mb-4 text-sm text-[var(--fg-muted)]">Select an agency in the header to load commissions.</p>
        ) : null}

        <div className="space-y-4">
          <FundingCommissionFilters
            search={search}
            status={status}
            payoutStatus={payoutStatus}
            clawbackRisk={clawbackRisk}
            assignedUserId={assignedUserId}
            payoutDueFrom={payoutDueFrom}
            payoutDueTo={payoutDueTo}
            onSearchChange={setSearch}
            onStatusChange={setStatus}
            onPayoutStatusChange={setPayoutStatus}
            onClawbackRiskChange={setClawbackRisk}
            onAssignedUserChange={setAssignedUserId}
            onPayoutDueFromChange={setPayoutDueFrom}
            onPayoutDueToChange={setPayoutDueTo}
          />

          {error ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200">
              {error}
            </div>
          ) : null}

          {loading ? (
            <p className="text-sm text-[var(--fg-muted)]">Loading commissions...</p>
          ) : filteredCommissions.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-10 text-center">
              <p className="text-sm font-medium text-[var(--fg-text)]">No commission records yet</p>
              <p className="mt-1 text-sm text-[var(--fg-muted)]">Create a commission from a funded deal in the pipeline.</p>
            </div>
          ) : viewMode === 'table' ? (
            <FundingCommissionTable
              commissions={filteredCommissions}
              onView={setSelectedCommission}
              onStatusChange={handleStatusChange}
              onDelete={handleDelete}
              savingId={savingId}
            />
          ) : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {filteredCommissions.map((commission) => (
                <FundingCommissionCard key={commission.id} commission={commission} onSelect={setSelectedCommission} />
              ))}
            </div>
          )}
        </div>
      </FundingPanel>

      <div className="mt-4 grid gap-4 xl:grid-cols-[1fr_1fr]">
        <FundingPanel title="Payout due" subtitle="Upcoming pending payout commissions">
          <FundingCommissionPayoutList commissions={filteredCommissions} onSelect={setSelectedCommission} />
        </FundingPanel>

        <FundingPanel title="Clawback monitoring" subtitle="Commissions under clawback risk review">
          <FundingCommissionClawbackPanel commissions={filteredCommissions} onSelect={setSelectedCommission} />
        </FundingPanel>
      </div>

      {selectedCommission ? (
        <FundingPanel title="Commission detail" subtitle="Status, payout, and clawback monitoring">
          <FundingCommissionDetail
            commission={selectedCommission}
            onClose={() => setSelectedCommission(null)}
            onStatusChange={handleStatusChange}
            saving={savingId === selectedCommission.id}
          />
        </FundingPanel>
      ) : null}
    </FundingSectionPage>
  );
};

export default FundingCommissionsPage;
